import { Invoice, InvoiceUpdateData, LineItem, lineItemType } from "../../../../../pojo/Pricing";

export namespace InvoiceMove {
    /** An occurrence or a requirement - the two things a user can move between invoices. */
    export type MovableItem =
        | { rsrvId: number; requirementId?: undefined }
        | { requirementId: number; rsrvId?: undefined };

    export type OccurrenceObjects = { spaceIds: Set<number>; resourceIds: Set<number>; eventType: boolean };

    export type InvoiceObjectIds = {
        rsrvIds: Set<number>;
        reqIds: Set<number>;
        spaceIds: Set<number>;
        resourceIds: Set<number>;
        includeEventType: boolean;
        byRsrvId: Map<number, OccurrenceObjects>;
    };

    /**
     * The billDefn object lists the API wants back on a move, derived from what the invoice actually
     * bills rather than from `billDefn` itself, which the server only rewrites from the values a PUT
     * sends and so can drift out of step with the line items.
     */
    export function invoiceObjectIds(invoice: Invoice | undefined): InvoiceObjectIds {
        const ids: InvoiceObjectIds = {
            rsrvIds: new Set(),
            reqIds: new Set(),
            spaceIds: new Set(),
            resourceIds: new Set(),
            includeEventType: !!invoice?.billDefn?.includeEventType,
            byRsrvId: new Map(),
        };

        for (const lineItem of invoice?.billing?.lineItems ?? []) {
            const itemId = lineItem.itemId;
            if (itemId) {
                if (lineItem.itemType === lineItemType.REQUIREMENT) ids.reqIds.add(itemId);
                else if (lineItem.itemType === lineItemType.LOCATION) ids.spaceIds.add(itemId);
                else if (lineItem.itemType === lineItemType.RESOURCE) ids.resourceIds.add(itemId);
            }

            for (const occurrence of lineItem.occurrences ?? []) {
                ids.rsrvIds.add(occurrence.rsrvId);
                const objects = ids.byRsrvId.get(occurrence.rsrvId) ?? {
                    spaceIds: new Set<number>(),
                    resourceIds: new Set<number>(),
                    eventType: false,
                };
                if (lineItem.itemType === lineItemType.LOCATION && itemId) objects.spaceIds.add(itemId);
                else if (lineItem.itemType === lineItemType.RESOURCE && itemId) objects.resourceIds.add(itemId);
                else if (lineItem.itemType === lineItemType.EVENT) objects.eventType = true;
                ids.byRsrvId.set(occurrence.rsrvId, objects);
            }
        }

        return ids;
    }

    export function isDateRangeInvoice(invoice: Invoice | undefined) {
        return !!invoice?.billDefn?.startDt && !!invoice?.billDefn?.endDt;
    }

    type PayloadIds = Pick<InvoiceObjectIds, "rsrvIds" | "reqIds" | "spaceIds" | "resourceIds" | "includeEventType">;

    function invoicePayload(invoiceId: number, ids: PayloadIds): Partial<InvoiceUpdateData> {
        const rsrvIdList = [...ids.rsrvIds],
            requirementIdList = [...ids.reqIds];

        return {
            billId: invoiceId,
            op: "update",
            emptyRsrvList: !rsrvIdList.length && !ids.includeEventType,
            includeEventType: ids.includeEventType,
            includeSpaces: !!rsrvIdList.length,
            includeResources: !!rsrvIdList.length,
            requirementIdList,
            includeRequirements: !!requirementIdList.length,
            rsrvIdList,
            ...(!!rsrvIdList.length && {
                resourceIdList: [...ids.resourceIds],
                spaceIdList: [...ids.spaceIds],
            }),
        };
    }

    function withItemAdded(
        target: InvoiceObjectIds,
        item: MovableItem,
        moved: OccurrenceObjects | undefined,
    ): PayloadIds {
        if (item.requirementId) {
            return { ...target, reqIds: new Set([...target.reqIds, item.requirementId]) };
        }

        return {
            ...target,
            rsrvIds: new Set([...target.rsrvIds, item.rsrvId!]),
            // The occurrence's spaces and resources have to join the target's lists, or its line items
            // arrive without the objects that priced them.
            spaceIds: new Set([...target.spaceIds, ...(moved?.spaceIds ?? [])]),
            resourceIds: new Set([...target.resourceIds, ...(moved?.resourceIds ?? [])]),
        };
    }

    function withItemRemoved(source: InvoiceObjectIds, item: MovableItem): PayloadIds {
        const remaining = { ...source };
        if (item.requirementId) {
            remaining.reqIds = new Set(source.reqIds);
            remaining.reqIds.delete(item.requirementId);
        } else {
            remaining.rsrvIds = new Set(source.rsrvIds);
            remaining.rsrvIds.delete(item.rsrvId!);
        }
        return remaining;
    }

    /**
     * Add to the target first, then remove from the source, so the line items exist somewhere for the
     * whole exchange. Omitting the target removes the item from its invoice outright.
     */
    export function buildMovePayloads(args: {
        item: MovableItem;
        source: Invoice;
        target?: Invoice;
    }): Partial<InvoiceUpdateData>[] {
        const { item, source, target } = args,
            sourceIds = invoiceObjectIds(source);

        const payloads: Partial<InvoiceUpdateData>[] = [];
        if (target) {
            const moved = item.rsrvId ? sourceIds.byRsrvId.get(item.rsrvId) : undefined;
            payloads.push(invoicePayload(target.id, withItemAdded(invoiceObjectIds(target), item, moved)));
        }
        payloads.push(invoicePayload(source.id, withItemRemoved(sourceIds, item)));

        return payloads;
    }

    /**
     * A moved occurrence can take the last billing of a space or resource with it, in which case the
     * source invoice drops that line item - and its adjustment - whatever the user asks for.
     */
    export function orphanedAdjustmentKeys(adjustedItems: LineItem[], item: MovableItem, source: Invoice): Set<string> {
        const keys = new Set<string>();
        if (!item.rsrvId) return keys;

        const sourceIds = invoiceObjectIds(source),
            remainingRsrvIds = [...sourceIds.rsrvIds].filter((rsrvId) => rsrvId !== item.rsrvId);
        if (!remainingRsrvIds.length) return keys;

        const stillBilled = (predicate: (objects: OccurrenceObjects) => boolean) =>
            remainingRsrvIds.some((rsrvId) => {
                const objects = sourceIds.byRsrvId.get(rsrvId);
                return !!objects && predicate(objects);
            });

        for (const lineItem of adjustedItems) {
            const itemId = lineItem.itemId!;
            const remains =
                lineItem.itemType === lineItemType.LOCATION
                    ? stillBilled((objects) => objects.spaceIds.has(itemId))
                    : lineItem.itemType === lineItemType.RESOURCE
                      ? stillBilled((objects) => objects.resourceIds.has(itemId))
                      : lineItem.itemType === lineItemType.EVENT
                        ? stillBilled((objects) => objects.eventType)
                        : true;
            if (!remains) keys.add(adjustmentKey(lineItem));
        }

        return keys;
    }

    export function adjustmentKey(lineItem: Pick<LineItem, "itemId" | "itemType">) {
        return `${lineItem.itemType}-${lineItem.itemId}`;
    }

    /**
     * Line item adjustments the move puts in play: the ones the moved item carries on the source
     * invoice. Event type items only come along when the target bills event types at all - pass
     * `targetIncludesEventType` when the target is still being created and has no `billDefn` yet.
     */
    export function adjustmentsInPlay(args: {
        item: MovableItem;
        source: Invoice;
        target?: Invoice;
        targetIncludesEventType?: boolean;
    }): LineItem[] {
        const { item, source, target } = args;
        const targetBillsEventType = args.targetIncludesEventType ?? !!target?.billDefn?.includeEventType;

        return (source.billing?.lineItems ?? []).filter((lineItem) => {
            if (lineItem.adjustmentAmt === undefined && lineItem.adjustmentPercent === undefined) return false;
            if (item.requirementId) {
                return lineItem.itemType === lineItemType.REQUIREMENT && lineItem.itemId === item.requirementId;
            }
            if (lineItem.itemType === lineItemType.EVENT && !targetBillsEventType) return false;
            return !!lineItem.occurrences?.some((occurrence) => occurrence.rsrvId === item.rsrvId);
        });
    }
}
